/**
 * Keeping the checkout token across the trip to a payment gateway —
 * BUILD-PLAN §26, §29.
 *
 * ## The bug this exists to fix
 *
 * A guest's authority to act on their own order is the checkout token, handed
 * back once when the order is placed. It was carried to the confirmation page
 * in React Router's `location.state`, which is fine right up until the
 * customer actually pays.
 *
 * Paying means leaving for `checkout.stripe.com` and coming back — a full
 * document navigation from another origin. React state, router state and
 * every in-memory value are gone by then. So the confirmation page loaded
 * with `checkoutToken = null`, the "did this payment succeed?" call was
 * skipped for want of authorisation, and a customer whose card had genuinely
 * been charged sat on a page that never confirmed. The server was right the
 * whole time; nothing ever asked it.
 *
 * `sessionStorage` survives that round trip while staying scoped to the tab
 * and origin, and it is dropped when the order settles or the tab closes —
 * so the capability lives exactly as long as the payment it authorises.
 *
 * Every access is wrapped: Safari's private mode and hardened browser
 * profiles throw on storage access, and losing the token must degrade to
 * "wait for the webhook", never crash the confirmation page.
 */

const KEY_PREFIX = 'glanz.checkout-token.';

function keyFor(orderNumber: string): string {
  return `${KEY_PREFIX}${orderNumber}`;
}

export function rememberCheckoutToken(orderNumber: string, token: string | undefined): void {
  if (!token) return;

  try {
    window.sessionStorage.setItem(keyFor(orderNumber), token);
  } catch {
    // Storage unavailable — the webhook remains the fallback.
  }
}

export function recallCheckoutToken(orderNumber: string): string | null {
  try {
    return window.sessionStorage.getItem(keyFor(orderNumber));
  } catch {
    return null;
  }
}

/**
 * Drop the token once it can do no more good.
 *
 * Called when the order is settled. Holding a capability after the thing it
 * authorises is finished is the definition of an unnecessary one.
 */
export function forgetCheckoutToken(orderNumber: string): void {
  try {
    window.sessionStorage.removeItem(keyFor(orderNumber));
  } catch {
    // Nothing to do; it expires with the tab regardless.
  }
}
